import { z } from 'zod';
import { presetManagementModeSchema, type PresetFieldDrop } from './onu-config.js';

/**
 * ONU migration: an ODF or an ODF port is repatched onto a different OLT, and
 * every ONU behind that feed has to be brought up on the new chassis carrying
 * the configuration it had on the old one.
 *
 * The dangerous half of this is that it AUTHORISES. Authorising is what puts a
 * subscriber into service, and doing it from a rule rather than from a person
 * is how one bad reading sweeps a fleet. So the shape here is deliberately
 * inverted from "watch and act":
 *
 *  - the PLAN is the feature. Nothing is written until an operator has seen
 *    exactly which ONUs would move and what each one would be given;
 *  - the armed window is only permission to run that plan, it expires by
 *    stored timestamp, it is scoped, and it is capped;
 *  - detection refuses anything it is not certain about. An ONU still live on
 *    the old chassis is a DUPLICATE, not a move, and an old chassis that could
 *    not be asked is not evidence that the ONU left it.
 */

// --- The armed auto-authorise window -------------------------------------

/**
 * How long a window may be armed for, in minutes — the operator's own fixed
 * choices. A free-text duration is still not offered: "armed until I remember"
 * is the failure this whole design is against, so a window always names an
 * expiry it closes itself at. The short options cover a single repatch shift;
 * the multi-day ones (added 2026-09-02 at the operator's request) cover a
 * campaign that spans days — a whole estate re-cabled ward by ward — where
 * re-arming every shift is the bigger risk. The cap and disarm-anytime are what
 * keep a long window bounded, not its length.
 */
export const AUTO_AUTHORIZE_DURATIONS = [30, 240, 480, 720, 1440, 2880, 10080] as const;
export type AutoAuthorizeDuration = (typeof AUTO_AUTHORIZE_DURATIONS)[number];

export const AUTO_AUTHORIZE_DURATION_LABELS: Record<AutoAuthorizeDuration, string> = {
  30: '30 minutes',
  240: '4 hours',
  480: '8 hours',
  720: '12 hours',
  1440: '24 hours',
  2880: '48 hours',
  10080: '7 days',
};

/**
 * The most ONUs ONE armed window may ever act on.
 *
 * 32 by default because that is the operator's unit of repatching: a feed is a
 * splitter chain, and 1×32 is the largest splitter in the topology model. A
 * whole-feed move fits; a detection bug that starts matching serials it should
 * not cannot get past a splitter's worth of subscribers before the window
 * refuses and says so.
 *
 * 128 is the hard ceiling because that is one full PON port — the largest
 * physical thing a single repatch can carry. No legitimate window needs more,
 * and a fleet of 4,657 subscribers is 36 times that, so the cap is what keeps
 * one bad window an incident rather than an outage.
 */
export const AUTO_AUTHORIZE_DEFAULT_CAP = 32;
export const AUTO_AUTHORIZE_MAX_CAP = 128;

export const armAutoAuthorizeSchema = z.object({
  durationMinutes: z.union([
    z.literal(30),
    z.literal(240),
    z.literal(480),
    z.literal(720),
    z.literal(1440),
    z.literal(2880),
    z.literal(10080),
  ]),
  /**
   * Required, not optional.
   *
   * An automatic authorisation has nobody standing there to answer "which
   * VLAN, which package, which ONU type", so the answers have to exist before
   * the window opens. A window with no preset could only guess, and guessing
   * is the thing this must never do.
   */
  presetId: z.string().min(1).max(64),
  /** Only accept ONUs whose SimpleACS record is on this OLT. Null = any. */
  sourceOltId: z.string().max(64).nullable().optional(),
  /**
   * Narrow the source further: only ONUs whose record sits on one of these
   * shelf/slot cards (e.g. `1/3`) of `sourceOltId`. Null or empty = every slot
   * of that OLT. Meaningless without a source OLT, and refused without one —
   * a slot number is only a name inside one chassis. SOURCE-side, where
   * `ponPort` is destination-side; the two combine.
   */
  sourceSlots: z
    .array(z.string().regex(/^\d+\/\d+$/))
    .max(64)
    .nullable()
    .optional(),
  /** Only accept ONUs that turn up on this PON port of the new OLT. */
  ponPort: z
    .string()
    .regex(/^\d+\/\d+\/\d+$/)
    .nullable()
    .optional(),
  maxOnus: z.coerce
    .number()
    .int()
    .min(1)
    .max(AUTO_AUTHORIZE_MAX_CAP)
    .default(AUTO_AUTHORIZE_DEFAULT_CAP),
  /**
   * Persist the new chassis's running config to flash once a move has landed.
   *
   * Deliberately NOT "remove the ONU from the old chassis": a write to the OLT
   * a subscriber may still be hanging off is the one thing a repatch must not
   * do automatically. Cleaning up the old side stays a deliberate deauthorise.
   */
  finalize: z.boolean().default(false),
});
export type ArmAutoAuthorizeRequest = z.infer<typeof armAutoAuthorizeSchema>;

export const disarmAutoAuthorizeSchema = z.object({
  reason: z.string().max(200).optional(),
});

export const autoAuthorizeWindowSchema = z.object({
  id: z.string(),
  oltId: z.string(),
  oltName: z.string().nullable(),
  orgId: z.string().nullable(),
  armedAt: z.number().int(),
  /** The only thing that decides whether a window is live. */
  expiresAt: z.number().int(),
  durationMinutes: z.number().int(),
  presetId: z.string(),
  presetName: z.string().nullable(),
  sourceOltId: z.string().nullable(),
  sourceOltName: z.string().nullable(),
  /** Source-side shelf/slot filter (`1/3`, …); null = every slot of the source. */
  sourceSlots: z.array(z.string()).nullable(),
  ponPort: z.string().nullable(),
  maxOnus: z.number().int(),
  /** ONUs this window has already acted on, against `maxOnus`. */
  usedCount: z.number().int(),
  finalize: z.boolean(),
  armedBy: z.string().nullable(),
  disarmedAt: z.number().int().nullable(),
  disarmedBy: z.string().nullable(),
  disarmReason: z.string().nullable(),
  lastScanAt: z.number().int().nullable(),
  /** Derived at read time from `expiresAt`, never from a timer. */
  active: z.boolean(),
  remainingMs: z.number().int(),
  createdAt: z.number().int(),
});
export type AutoAuthorizeWindow = z.infer<typeof autoAuthorizeWindowSchema>;

// --- Where an ONU sits, and what kind of move it made ---------------------

/**
 * A PON port is `shelf/slot/port` on BOTH vendors, and the middle component is
 * the card — the board an ONU's fibre terminates on.
 *
 * The bases differ and are never assumed: the operator's ZTE chassis number
 * shelves from 1 (`1/2/1` … `1/8/9`) and their Huawei chassis from 0 (`0/0/0`
 * … `0/4/7`). So the components are parsed and compared, never the string —
 * string comparison can tell "changed" from "unchanged" and cannot tell a
 * re-patch to the next port on the same card from a whole board being
 * re-cabled, which are different operations with different blast radii.
 */
export interface PonSlot {
  shelf: number;
  /** The card / board. This is the component a board swap changes. */
  slot: number;
  port: number;
}

export function parsePonSlot(ponPort: string | null | undefined): PonSlot | null {
  const m = /^(\d+)\/(\d+)\/(\d+)$/.exec((ponPort ?? '').trim());
  if (!m) return null;
  return { shelf: Number(m[1]), slot: Number(m[2]), port: Number(m[3]) };
}

/**
 * The three move classes the operator actually has, plus "no move".
 *
 * All three are within one organization and all three are handled the same
 * way; the class exists because an operator reading a plan needs to know
 * whether they are looking at one subscriber re-patched to the next port or a
 * whole board being re-cabled — a board move carries every ONU on that card at
 * once, which is exactly when a cap earns its keep.
 */
export const moveClassSchema = z.enum([
  /** Same chassis, same card, different port. */
  'port',
  /** Same chassis, different card. A board swap or a re-cable. */
  'board',
  /** A different chassis entirely. The ODF-repatched-to-another-OLT case. */
  'chassis',
  /** Same chassis, same slot. Not a move — a re-authorisation. */
  'none',
  /** A port this platform cannot parse, so the class cannot be stated. */
  'unknown',
]);
export type MoveClass = z.infer<typeof moveClassSchema>;

export const MOVE_CLASS_LABELS: Record<MoveClass, string> = {
  port: 'Port move (same card)',
  board: 'Board move (same OLT)',
  chassis: 'Different OLT',
  none: 'Same slot',
  unknown: 'Unrecognised port',
};

export function classifyMove(
  from: { oltId: string; ponPort: string | null },
  to: { oltId: string; ponPort: string | null },
): MoveClass {
  if (from.oltId !== to.oltId) return 'chassis';
  const a = parsePonSlot(from.ponPort);
  const b = parsePonSlot(to.ponPort);
  if (!a || !b) return 'unknown';
  if (a.shelf !== b.shelf || a.slot !== b.slot) return 'board';
  if (a.port !== b.port) return 'port';
  return 'none';
}

// --- Detection ------------------------------------------------------------

/**
 * What the OLD slot says about an ONU that has turned up somewhere else.
 *
 * The distinction between `live` and `declared_down` is the whole safety
 * argument. A repatch does NOT remove the old declaration — the config stays
 * on the old port until somebody deauthorises it — so "still configured there"
 * is the normal state after a move and cannot be the refusal test. What
 * refuses is the ONU still RANGING there: light is arriving at the old port,
 * so the fibre did not move, so this is two chassis claiming one serial.
 */
export const onuPresenceSchema = z.enum([
  /** Declared on the old slot AND still ranging there. A duplicate. */
  'live',
  /** Still declared on the old slot but dark — the fibre left. A move. */
  'declared_down',
  /** Not declared on the old slot at all. A move. */
  'absent',
  /** The old slot could not be read. Silence, not absence. */
  'unknown',
]);
export type OnuPresence = z.infer<typeof onuPresenceSchema>;

export const ONU_PRESENCE_LABELS: Record<OnuPresence, string> = {
  live: 'Still live on the old slot',
  declared_down: 'Still declared, but dark',
  absent: 'Gone from the old slot',
  unknown: 'Could not be checked',
};

/**
 * What one unconfigured ONU on a new chassis turns out to be.
 *
 * Only `migration` is ever acted on automatically. The rest are shown, with
 * their reason, because "why did this one not move" is the question an
 * operator asks next and an empty list cannot answer it.
 */
export const migrationVerdictSchema = z.enum([
  /** Recorded on another OLT, and that OLT no longer carries it. A move. */
  'migration',
  /**
   * Recorded on another OLT and STILL configured there. Two chassis claiming
   * one serial is a duplicate — either the same ONU is double-provisioned or
   * two units share a serial — and authorising it here would put a second
   * subscriber on a live one. Never automatic.
   */
  'duplicate',
  /**
   * The old chassis could not be asked. Silence is not absence: an OLT that is
   * unreachable, has no CLI credentials, or answered nothing about the port is
   * exactly the case where assuming "it left" authorises over a live service.
   */
  'unverified',
  /** No record anywhere. A new subscriber, and the wizard's job, not this. */
  'new_subscriber',
  /**
   * Recorded on this chassis, on this very slot. A re-authorisation, not a
   * move — the ONU has not gone anywhere. Note this is NOT "same OLT": a port
   * or board move within one chassis IS a migration and is handled as one.
   */
  'same_slot',
  /**
   * A genuine move, refused because the preset could not carry every field to
   * this chassis. A repatch onto a different-vendor OLT is exactly that case —
   * the ONU type does not travel between vendors — and authorising with the
   * hole would put a subscriber on service configured with less than the
   * operator asked for, which nobody would notice until they called.
   */
  'preset_incomplete',
  /** Past the window's cap, or past the plan's cap. Shown, never acted on. */
  'capped',
  /** Outside the window's scope (source OLT or PON port). */
  'out_of_scope',
]);
export type MigrationVerdict = z.infer<typeof migrationVerdictSchema>;

export const MIGRATION_VERDICT_LABELS: Record<MigrationVerdict, string> = {
  migration: 'Will migrate',
  duplicate: 'Duplicate — still live on the old OLT',
  unverified: 'Old OLT could not be checked',
  new_subscriber: 'New subscriber',
  same_slot: 'Already on this slot',
  preset_incomplete: 'Preset does not fit this chassis',
  capped: 'Over the cap',
  out_of_scope: 'Outside the armed scope',
};

/** Only one verdict is ever executed. Everything else is information. */
export function isActionable(verdict: MigrationVerdict): boolean {
  return verdict === 'migration';
}

/**
 * A SimpleACS record for a serial that has turned up unconfigured on a
 * DIFFERENT slot — the cheap, read-only half of the migration verdict.
 *
 * The "Awaiting authorisation" list carries one of these per waiting ONU so it
 * can LABEL a repatch as a move rather than a new subscriber: authorising a
 * moved subscriber as if they were new mints a fresh record and strands the
 * old one (its number, its events, its optical history). It is a hint, never a
 * decision — the authoritative duplicate / presence check still happens when
 * the migration plan reads the old slot's light. A record on the very slot the
 * ONU is appearing on is a re-authorisation, not a move, and is never one of
 * these.
 */
export const knownOnuRecordSchema = z.object({
  oltId: z.string(),
  oltName: z.string(),
  ponPort: z.string(),
  onuIndex: z.string(),
  name: z.string().nullable(),
  number: z.number().int().nullable(),
  /** A retired unit kept as history, rather than a live record. */
  removed: z.boolean(),
});
export type KnownOnuRecord = z.infer<typeof knownOnuRecordSchema>;

/**
 * The four actions from the operator's reference, in the order they have to
 * run.
 *
 * `authorize` writes the new chassis. `move` repoints SimpleACS's own record
 * and re-anchors the splitter chain, and has to follow the authorise because
 * the ONU id is only known once the chassis has taken it. `resync` pushes the
 * VLANs and ports the ONU already had onto the new chassis — this is the
 * "config carried across" — and has to follow the move, because it renders
 * against whichever OLT the record now points at. `finalize` saves the new
 * chassis's running config to flash.
 */
export const migrationActionSchema = z.enum([
  'authorize',
  // A move within ONE chassis (port or board, 2026-10-08): the new slot is built
  // from the lines the ONU's old declaration holds — its T-CONTs, GEM ports,
  // rates and service-ports — and the old declaration is cleared once it is.
  'carry',
  'clear_old',
  'move',
  'resync',
  // The second management write, when a preset chose the mode — points the moved
  // ONU at the ACS (TR-069) or takes it off (OMCI). Between resync and finalize
  // so its lines are on the chassis before the running config is saved to flash.
  'management',
  'finalize',
]);
export type MigrationAction = z.infer<typeof migrationActionSchema>;

export const MIGRATION_ACTION_ORDER: MigrationAction[] = [
  'authorize',
  'carry',
  'clear_old',
  'move',
  'resync',
  'management',
  'finalize',
];

// --- The plan -------------------------------------------------------------

/** What one candidate would become, if the plan were run. */
export const migrationPlanItemSchema = z.object({
  serial: z.string(),
  verdict: migrationVerdictSchema,
  /** port | board | chassis — which of the operator's three cases this is. */
  moveClass: moveClassSchema,
  /** Plain sentence naming the evidence behind the verdict. */
  reason: z.string(),
  /** Where it turned up on the destination chassis. */
  toPonPort: z.string(),
  /** The id the new chassis would give it — read from the chassis, not the DB. */
  toOnuId: z.number().int().nullable(),
  /** The SimpleACS record this serial matched, when there is one. */
  onuId: z.string().nullable(),
  onuNumber: z.number().int().nullable(),
  name: z.string().nullable(),
  fromOltId: z.string().nullable(),
  fromOltName: z.string().nullable(),
  fromPonPort: z.string().nullable(),
  fromOnuIndex: z.string().nullable(),
  /** What the old slot said when asked whether it still carries this ONU. */
  presence: onuPresenceSchema,
  /**
   * A stale declaration left on the old slot.
   *
   * True when the ONU is still configured where it was but dark. The move goes
   * ahead — that is what a repatch looks like — and this is surfaced so the
   * operator knows there is a deauthorise waiting on the old side. It is NOT
   * done automatically: writing to the chassis a subscriber might still be
   * hanging off is the one thing a repatch must never do by itself.
   */
  staleOnOldSlot: z.boolean().default(false),
  /** The config it would be authorised with, after the preset is resolved. */
  config: z
    .object({
      onuType: z.string().nullable(),
      dataVlan: z.number().int().nullable(),
      managementVlan: z.number().int().nullable(),
      vlanMode: z.string(),
      addressing: z.string(),
      upstreamProfileId: z.string().nullable(),
      downstreamProfileId: z.string().nullable(),
      zoneId: z.string().nullable(),
      /**
       * How the ONU should be managed after the move, when a PRESET deliberately
       * set it — `tr069` points the moved ONU at the ACS, `omci` takes it off.
       * Null on a preset-less move: that carries the ONU's OWN stored mode, which
       * the post-move resync already honours, so nothing here overrides it. When
       * it names a mode, the executor performs the second management write (the
       * `tr069-mgmt … acs …` stack the authorise wizard now sends by default),
       * so a migrated ONU never comes up with a management IP and no ACS pointer.
       */
      managementMode: presetManagementModeSchema.nullable().default(null),
    })
    .nullable(),
  /**
   * Fields the preset named that this chassis could not honour — B's rule.
   *
   * A repatch onto a different-vendor OLT is exactly the cross-vendor case, so
   * these are surfaced as their own field rather than folded into warnings: an
   * ONU whose type was dropped must not be authorised with a hole in it.
   */
  dropped: z
    .array(
      z.object({
        field: z.string(),
        wanted: z.union([z.string(), z.number()]),
        reason: z.string(),
      }),
    )
    .default([]),
  /** Anything the operator should read before approving. Worst first. */
  warnings: z.array(z.string()).default([]),
  /** The exact commands the authorise would send, so a plan is a receipt. */
  commands: z.array(z.string()).default([]),
  /**
   * VLANs this ONU would carry that the DESTINATION OLT does not have.
   *
   * A cross-OLT move brings the subscriber's own VLANs, and the new chassis may
   * not carry one — the frames would then reach a dead end. Each of these needs
   * a resolution before the move: add it to an uplink on the destination, or
   * replace it on the ONU with a VLAN the destination already has. Empty when
   * everything the ONU carries is present on the destination (always so for a
   * same-OLT port or board move).
   */
  missingVlans: z.array(z.number().int()).default([]),
  /** Where the light path would end up. */
  topology: z
    .object({
      splitterId: z.string().nullable(),
      splitterName: z.string().nullable(),
      /** The chain's root, which is the thing an OLT port anchors to. */
      rootId: z.string().nullable(),
      currentOltId: z.string().nullable(),
      currentPonPort: z.string().nullable(),
      /** True when the chain has to be re-anchored to follow the feed. */
      willReanchor: z.boolean(),
    })
    .nullable(),
});
export type MigrationPlanItem = z.infer<typeof migrationPlanItemSchema>;

export const migrationPlanSchema = z.object({
  oltId: z.string(),
  oltName: z.string(),
  vendor: z.string(),
  presetId: z.string().nullable(),
  presetName: z.string().nullable(),
  /** The cap this plan was built against, so the number is on screen. */
  cap: z.number().int(),
  /** Candidates that would actually move. Never more than `cap`. */
  actionable: z.number().int(),
  items: z.array(migrationPlanItemSchema),
  warnings: z.array(z.string()),
  /**
   * The VLANs the DESTINATION OLT already carries — the "local" VLANs a missing
   * one can be replaced with, so the card's picker needs no extra fetch.
   */
  destinationVlanIds: z.array(z.number().int()).default([]),
  /** Null when nothing is armed — the plan is readable either way. */
  window: autoAuthorizeWindowSchema.nullable(),
  builtAt: z.number().int(),
});
export type MigrationPlan = z.infer<typeof migrationPlanSchema>;

/**
 * How a missing VLAN on the destination is resolved for a cross-OLT move.
 *
 * The operator's choice, per the ONU list card: either the subscriber keeps
 * their VLAN and it is added to an uplink on the destination so it reaches the
 * core, or the subscriber is moved onto a VLAN the destination already has.
 * Applied per VLAN — a VLAN missing across many ONUs is one decision.
 */
export const vlanResolutionSchema = z.object({
  /** The missing VLAN this resolves. */
  vlanId: z.number().int().min(1).max(4094),
  action: z.enum([
    /** Create the VLAN on the destination and tag it on `uplinkIfName`. */
    'add-to-uplink',
    /** Move the subscriber onto `localVlanId`, which the destination carries. */
    'replace',
  ]),
  /** Required for `replace`: an existing destination VLAN. */
  localVlanId: z.number().int().min(1).max(4094).optional(),
  /** Required for `add-to-uplink`: the uplink port to tag it on. */
  uplinkIfName: z.string().max(64).optional(),
});
export type MigrationVlanResolution = z.infer<typeof vlanResolutionSchema>;

export const runMigrationSchema = z.object({
  /** Serials to move. Empty is refused rather than treated as "all". */
  serials: z.array(z.string().min(4).max(32)).min(1).max(AUTO_AUTHORIZE_MAX_CAP),
  /** Overrides the armed window's preset when an operator is driving. */
  presetId: z.string().max(64).optional(),
  /**
   * How each VLAN the destination lacks is resolved — see `vlanResolutionSchema`.
   * A move whose ONUs carry a missing VLAN with no resolution here is refused,
   * so a subscriber is never moved onto a chassis that would black-hole them.
   */
  vlanResolutions: z.array(vlanResolutionSchema).max(64).optional(),
  finalize: z.boolean().default(false),
  /** Default true: this authorises subscribers, so it says what it would do. */
  dryRun: z.boolean().default(true),
});
export type RunMigrationRequest = z.infer<typeof runMigrationSchema>;

// --- The record -----------------------------------------------------------

export const migrationStatusSchema = z.enum([
  'planned',
  'blocked',
  'running',
  'migrated',
  'failed',
]);
export type MigrationStatus = z.infer<typeof migrationStatusSchema>;

/** Who caused it. The whole point of recording it. */
export const migrationTriggerSchema = z.enum(['operator', 'armed_window']);
export type MigrationTrigger = z.infer<typeof migrationTriggerSchema>;

export const onuMigrationSchema = z.object({
  id: z.string(),
  orgId: z.string().nullable(),
  serial: z.string(),
  fromOltId: z.string().nullable(),
  fromOltName: z.string().nullable(),
  fromPonPort: z.string().nullable(),
  fromOnuIndex: z.string().nullable(),
  toOltId: z.string(),
  toOltName: z.string().nullable(),
  toPonPort: z.string().nullable(),
  toOnuIndex: z.string().nullable(),
  moveClass: moveClassSchema,
  onuId: z.string().nullable(),
  /** The platform number the subscriber keeps. A move never mints a new one. */
  onuNumber: z.number().int().nullable(),
  presetId: z.string().nullable(),
  presetName: z.string().nullable(),
  dropped: z.array(z.unknown()).default([]),
  actions: z.array(migrationActionSchema).default([]),
  status: migrationStatusSchema,
  blockedReason: z.string().nullable(),
  trigger: migrationTriggerSchema,
  windowId: z.string().nullable(),
  actor: z.string().nullable(),
  splitterId: z.string().nullable(),
  reanchored: z.boolean(),
  detail: z.unknown().nullable(),
  createdAt: z.number().int(),
  completedAt: z.number().int().nullable(),
});
export type OnuMigration = z.infer<typeof onuMigrationSchema>;

/** The report kind migrations are filed under, so the OLT log carries them. */
export const MIGRATION_REPORT_KIND = 'onu_migration';

/**
 * One sentence naming what the operator must check, given what a preset could
 * not carry to this chassis.
 *
 * Written here rather than in the API so the plan dialog and the migration
 * record read identically — a dropped ONU type is the same fact whether it is
 * being previewed or explained afterwards.
 */
export function describeDropped(dropped: PresetFieldDrop[]): string | null {
  if (dropped.length === 0) return null;
  const fields = dropped.map((d) => d.field).join(', ');
  return `This preset could not carry ${fields} to this chassis. Migrating without ${
    dropped.length === 1 ? 'it' : 'them'
  } would authorise the subscriber with a hole in their configuration, so it is refused until the plan is re-run with a preset that fits.`;
}
